/**
 * DataTable Constants
 *
 * Shared constants for DataTable components to avoid magic numbers
 * and repeated style values.
 */

import { SCROLLBAR_LANE_SIZE } from '@/react/components/ui/designed-scroll-area';

// ─────────────────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────────────────

/** Default number of items per page */
export const DEFAULT_PAGE_SIZE = 50;

/** Available per page options for pagination dropdown */
export const DEFAULT_PER_PAGE_OPTIONS = [50, 100, 150, 200] as const;

/** Maximum page numbers to show in pagination before ellipsis */
export const MAX_VISIBLE_PAGES = 5;

/** @deprecated Use MAX_VISIBLE_PAGES instead */
export const MAX_PAGINATION_PAGES = MAX_VISIBLE_PAGES;

// ─────────────────────────────────────────────────────────────────────────────
// Table Layout
// ─────────────────────────────────────────────────────────────────────────────

/** Default number of skeleton rows to show when loading */
export const DEFAULT_SKELETON_ROWS = 5;

/** Default TanStack Table column size (used to detect custom sizes) */
export const DEFAULT_COLUMN_SIZE = 150;

/** Width for the actions column (3-dot menu): 42px for the trigger and its
 *  padding, plus SCROLLBAR_LANE_SIZE for the LAST_CELL_GUTTER_CLASSES gutter. */
export const ACTIONS_COLUMN_WIDTH = 60 + SCROLLBAR_LANE_SIZE;

/** Approximate header height in pixels (for scroll calculations) */
export const TABLE_HEADER_HEIGHT = 49;

/** Approximate pagination footer height in pixels */
export const TABLE_FOOTER_HEIGHT = 57;

// ─────────────────────────────────────────────────────────────────────────────
// Styling Classes
// ─────────────────────────────────────────────────────────────────────────────

/** Common table cell padding (px-6 = 18px per custom Tailwind scale, matches Figma) */
export const TABLE_CELL_CLASSES = 'font-inter px-6 py-4 group/popout' as const;

/** Right padding for the last cell of every row (header included): the usual
 *  18px cell padding plus the 18px vertical scrollbar lane, which is an overlay
 *  pinned to the container's right edge. Without it the row-actions kebab and
 *  right-aligned values butt against the lane's divider — or slide under the
 *  lane entirely. `pr-12` = 36px on the 3px spacing scale. */
export const LAST_CELL_GUTTER_CLASSES = '[&_tr>*:last-child]:pr-12' as const;

/** Lets a long unbroken name, such as "A/B/C", wrap inside its column instead of spilling into the next one. */
export const WRAP_ANYWHERE_CLASS = '[overflow-wrap:anywhere]' as const;

/** Common border color class */
export const TABLE_BORDER_CLASSES = 'border-dinnerplate-white' as const;

/** Shared classes for the select-row / select-all checkbox in table columns. */
export const CHECKBOX_CLASSES = 'size-[18px] rounded-xs border-grey-light shadow-none data-[state=checked]:bg-off-black data-[state=checked]:border-off-black' as const;

/** Shared classes for a small label pill (used for product category, preset
 *  menu format, etc.). Pair with `<Badge variant="static">`. Figma spec:
 *  3px 6px padding, 5px radius, dinnerplate-white background. */
export const CATEGORY_PILL_CLASSES = 'font-inter w-fit border-0 rounded-xs bg-dinnerplate-white text-grey text-xs font-semibold px-2 py-1 flex items-center justify-center leading-tight-sm' as const;

/** Border radius for dropdowns and buttons (10px from design system) */
export const BORDER_RADIUS_CLASSES = 'rounded-input' as const;

/** Size of an interactive control in the page footer — page-number buttons, the
 *  ellipsis, and the InlineSelect trigger. 32px comes straight from Figma
 *  ("Global footer": 18px padding + 32px control + 18px padding + 1px divider =
 *  the 69px footer). It is deliberately an arbitrary value: 32 is off Grain's
 *  3px spacing scale, and adding an off-grid key to tailwind.config.js would
 *  contradict that file's own guidance. `min-w` rather than a fixed width so a
 *  multi-digit page ("125") grows, matching Figma's auto-width variant. */
export const FOOTER_CONTROL_SIZE_CLASSES = 'h-[32px] min-w-[32px]' as const;

/** Row actions menu width */
export const ROW_ACTIONS_MENU_WIDTH = 220;

/** Row actions menu positioning — opens to the left of the trigger, vertically centered */
export const ROW_ACTIONS_MENU_PROPS = {
  side: 'left',
  align: 'center',
  sideOffset: 4,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Animations
// ─────────────────────────────────────────────────────────────────────────────

/** Framer Motion spring animation config for tabs */
export const TAB_ANIMATION_CONFIG = {
  type: 'spring' as const,
  bounce: 0.2,
  duration: 0.4,
};

/** Shared dropdown animation classes (blur + scale + slide) */
export const DROPDOWN_ANIMATION_CLASSES = 
  'animate-dropdown-in data-[state=closed]:animate-dropdown-out';

// ─────────────────────────────────────────────────────────────────────────────
// Icon Sizes
// ─────────────────────────────────────────────────────────────────────────────

/** Icon size for search bar (15px — spacing-5 in design system) */
export const ICON_SIZE_SEARCH = 'w-5 h-5' as const;

// ─────────────────────────────────────────────────────────────────────────────
// Keyboard
// ─────────────────────────────────────────────────────────────────────────────

/** Throttle delay for keyboard shortcuts (ms) */
export const SHORTCUT_THROTTLE_MS = 200;

/** Throttle delay for panel-opening actions (ms) */
export const PANEL_THROTTLE_MS = 300;

// ─────────────────────────────────────────────────────────────────────────────
// Virtualization
// ─────────────────────────────────────────────────────────────────────────────

/** Row count threshold above which virtualization auto-enables */
export const VIRTUALIZATION_THRESHOLD = 50;

/**
 * Default estimated row height in pixels for virtualization (actual height is
 * measured dynamically once a row renders). Rows differ per table, so a table
 * whose real height is known passes `estimatedRowHeight` to `DataTableContent`
 * rather than moving this shared default.
 */
export const ESTIMATED_ROW_HEIGHT = 57;

